import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  Typography,
  TextField,
  Box,
  Button,
  IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {
  getFirestore,
  collection,
  addDoc,
  getDocs,
  query,
  where,
  doc,
  getDoc,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import CustomerSelectionTable from './CustomerSelectionTable';
import ConflictResolutionModal from './ConflictResolutionModal';
import { auth } from './firebaseConfig';

/**
 * Çalışma süresini dakikaya çeviren fonksiyon - iOS HopsPlus ile AYNI MANTIK
 */
const parseWorkingTime = (timeString) => {
  if (!timeString || timeString.toString().trim() === '') return 0;
  const input = timeString.toString().trim();
  if (input.includes(':') && !input.toLowerCase().includes('saat')) {
    const parts = input.split(':');
    if (parts.length === 2) {
      return (parseInt(parts[0].trim(), 10) || 0) * 60 + (parseInt(parts[1].trim(), 10) || 0);
    }
  }
  if (input.toLowerCase().includes('saat')) {
    let hours = 0, minutes = 0;
    const saatMatch = input.toLowerCase().match(/(\d+)\s*saat/);
    if (saatMatch) hours = parseInt(saatMatch[1], 10) || 0;
    const dakikaMatch = input.toLowerCase().match(/(\d+)\s*(dakika|dk)/);
    if (dakikaMatch) minutes = parseInt(dakikaMatch[1], 10) || 0;
    return hours * 60 + minutes;
  }
  const normalized = input.replace(',', '.');
  const decimalHours = parseFloat(normalized);
  if (!isNaN(decimalHours) && normalized.includes('.')) return Math.round(decimalHours * 60);
  const justNumber = parseInt(input, 10);
  if (!isNaN(justNumber)) return justNumber;
  return 0;
};

const ModalBox = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 600,
  backgroundColor: '#ffffff',
  color: '#000000',
  boxShadow: theme.shadows[5],
  padding: theme.spacing(4),
  maxHeight: '80vh',
  overflowY: 'auto',
  border: '1px solid #ccc',
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(0.5),
  marginLeft: theme.spacing(1),
  width: '30px',
  height: '30px',
}));

const AddSantiyeModal = ({
  isOpen,
  onClose,
  selectedCustomer,
  setCustomerShantiyeler,
  customerShantiyeler,
  setAlertOpen,
  setError,
  setSuccessMessage,
}) => {
  const [santiyeName, setSantiyeName] = useState('');
  const [santiyePhone, setSantiyePhone] = useState('');
  const [santiyeEmail, setSantiyeEmail] = useState('');
  const [santiyeCariCode, setSantiyeCariCode] = useState('');
  const [isConflictModalOpen, setIsConflictModalOpen] = useState(false);
  const [conflictingSantiye, setConflictingSantiye] = useState(null);
  const [isCustomerSelectionOpen, setIsCustomerSelectionOpen] = useState(false);
  const [pendingCustomers, setPendingCustomers] = useState([]);

  const generateCariCode = useCallback(async () => {
    const db = getFirestore();
    const mainFirmCariCode = selectedCustomer?.cariCode;

    if (!mainFirmCariCode) {
      setError('Ana firmanın cari kodu bulunamadı.');
      setAlertOpen(true);
      return;
    }

    try {
      // Mevcut şantiyeleri getir ve cari kodlarını analiz et
      const shantiyeListRef = collection(db, 'müşteri listesi');
      const shantiyeQuery = query(
        shantiyeListRef,
        where('parentId', '==', selectedCustomer.id)
      );
      const shantiyeSnapshot = await getDocs(shantiyeQuery);
      
      // Mevcut şantiye numaralarını topla
      const existingNumbers = shantiyeSnapshot.docs
        .map(doc => {
          const cariCode = doc.data()['Şantiye Cari Kodu'] || '';
          const number = cariCode.split('/').pop();
          return number ? parseInt(number, 10) : 0;
        })
        .filter(num => !isNaN(num));

      // Boşluk kontrolü yap
      let nextNumber = 1;
      existingNumbers.sort((a, b) => a - b); // Sırala

      // Sıralı numaralarda ilk boşluğu bul
      for (let i = 0; i < existingNumbers.length; i++) {
        if (existingNumbers[i] !== i + 1) {
          nextNumber = i + 1;
          break;
        }
        nextNumber = i + 2;
      }

      const generatedCode = `${mainFirmCariCode}/${nextNumber}`;
      console.log('Mevcut şantiye numaraları:', existingNumbers);
      console.log('Yeni şantiye kodu:', generatedCode);

      setSantiyeCariCode(generatedCode);
    } catch (error) {
      console.error('Şantiyeler alınırken hata oluştu:', error);
      setError('Şantiyeler alınırken bir hata oluştu.');
      setAlertOpen(true);
    }
  }, [selectedCustomer, setAlertOpen, setError]);

  useEffect(() => {
    if (isOpen) {
      generateCariCode();
    }
  }, [isOpen, generateCariCode]);

  const handleAddSantiye = async () => {
    if (!santiyeName.trim()) {
      setError('Şantiye adı gereklidir.');
      setAlertOpen(true);
      return;
    }

    if (!santiyeCariCode.trim()) {
      setError('Cari kod gereklidir.');
      setAlertOpen(true);
      return;
    }

    await checkConflictsAndAdd();
  };

  const checkConflictsAndAdd = async () => {
    const db = getFirestore();
    try {
      // Şantiye adı çakışmasını kontrol et
      const santiyeListRef = collection(db, 'müşteri listesi');
      const nameConflictQuery = query(
        santiyeListRef,
        where('Şantiye Adı', '==', santiyeName.trim()),
        where('__name__', '!=', selectedCustomer?.id || '')
      );

      const nameConflictSnapshot = await getDocs(nameConflictQuery);

      if (!nameConflictSnapshot.empty) {
        const conflictingSantiye = {
          ...nameConflictSnapshot.docs[0].data(),
          id: nameConflictSnapshot.docs[0].id
        };

        // Veri aktarma modalını göster
        setConflictingSantiye(conflictingSantiye);
        setIsConflictModalOpen(true);
        return;
      }

      await addSantiyeToFirestore();
    } catch (error) {
      console.error('Çakışma kontrolü sırasında hata:', error);
      setError(`Çakışma kontrolü sırasında bir hata oluştu: ${error.message}`);
      setAlertOpen(true);
    }
  };

  const addSantiyeToFirestore = async () => {
    const db = getFirestore();
    try {
      // Cari kodun doğru s��rada olduğunu kontrol et
      const currentNumber = parseInt(santiyeCariCode.split('/').pop(), 10);
      const shantiyeQuery = query(
        collection(db, 'müşteri listesi'),
        where('parentId', '==', selectedCustomer.id)
      );
      const shantiyeSnapshot = await getDocs(shantiyeQuery);
      
      const existingNumbers = shantiyeSnapshot.docs
        .map(doc => {
          const cariCode = doc.data()['Şantiye Cari Kodu'] || '';
          const number = cariCode.split('/').pop();
          return number ? parseInt(number, 10) : 0;
        })
        .filter(num => !isNaN(num));

      // Eğer bu numara zaten kullanıldıysa, yeni bir kod generate et
      if (existingNumbers.includes(currentNumber)) {
        await generateCariCode(); // Yeni kod generate et
        setError('Şantiye kodu çakışması tespit edildi. Yeni kod oluşturuldu.');
        setAlertOpen(true);
        return;
      }

      const santiyeData = {
        'Müşteri Adı': santiyeName.trim(),
        Telefon: santiyePhone.trim(),
        'E-posta': santiyeEmail.trim(),
        cariCode: santiyeCariCode.trim(),
        Şantiye: true,
        parentId: selectedCustomer.id,
        Onay: 'Onaylandı',
        'Şantiye Adı': santiyeName.trim(),
        'Şantiye Cari Kodu': santiyeCariCode.trim(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // Eğer seçilen müşteri onay bekleyen listesinden geldiyse, o kaydı güncelleyelim
      const selectedPendingCustomer = pendingCustomers.find(
        customer => customer['Müşteri Adı'] === santiyeName.trim()
      );

      let docRef;
      if (selectedPendingCustomer) {
        // Var olan müşteriyi güncelle
        docRef = doc(db, 'müşteri listesi', selectedPendingCustomer.id);
        await updateDoc(docRef, santiyeData);
      } else {
        // Yeni şantiye ekle
        docRef = await addDoc(collection(db, 'müşteri listesi'), santiyeData);
      }

      // Ana müşterinin şantiye sayısını güncelle
      const parentRef = doc(db, 'müşteri listesi', selectedCustomer.id);
      const parentDoc = await getDoc(parentRef);
      const currentCount = parentDoc.data()?.santiyeCount || 0;
      await updateDoc(parentRef, {
        santiyeCount: currentCount + 1,
        updatedAt: new Date().toISOString()
      });

      setCustomerShantiyeler([
        ...customerShantiyeler,
        {
          id: selectedPendingCustomer ? selectedPendingCustomer.id : docRef.id,
          ...santiyeData
        },
      ]);

      setSuccessMessage('Şantiye başarıyla eklendi.');
      setAlertOpen(true);
      onClose();
      resetForm();
    } catch (error) {
      console.error('Şantiye eklenirken bir hata oluştu:', error);
      throw new Error('Şantiye eklenirken bir hata oluştu.');
    }
  };

  const resetForm = () => {
    setSantiyeName('');
    setSantiyePhone('');
    setSantiyeEmail('');
    // Cari kodu sıfırlamıyoruz, çünkü otomatik olarak oluşturuluyor
  };

  const handleSelectCustomer = (customer) => {
    setSantiyeName(customer['Müşteri Adı'] || '');
    setSantiyePhone(customer.Telefon || '');
    setSantiyeEmail(customer['E-posta'] || '');
    // Cari kodu seçilen müşteriden almıyoruz, otomatik oluşturulan kodu koruyoruz
    setIsCustomerSelectionOpen(false);
  };

  const fetchPendingCustomers = useCallback(async () => {
    const db = getFirestore();
    try {
      const customerListRef = collection(db, 'müşteri listesi');
      const pendingCustomersQuery = query(
        customerListRef,
        where('Onay', '==', 'Onay Bekliyor'),
      );
      const querySnapshot = await getDocs(pendingCustomersQuery);
      const customers = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setPendingCustomers(customers);
    } catch (error) {
      console.error('Pending customers could not be fetched:', error);
      setError('Onay bekleyen müşteriler getirilemedi.');
      setAlertOpen(true);
    }
  }, [setError, setAlertOpen]);

  const handleConflictResolution = async () => {
    setIsConflictModalOpen(false);
    const db = getFirestore();
    const batch = writeBatch(db);

    try {
      if (!conflictingSantiye || !conflictingSantiye.id) {
        throw new Error('Çakışan şantiye bilgisi eksik veya geçersiz.');
      }

      console.log('🔄 Şantiye birleştirme işlemi başlıyor:', {
        çakışanŞantiye: conflictingSantiye['Şantiye Adı'],
        yeniŞantiye: santiyeName.trim(),
        çakışanId: conflictingSantiye.id
      });

      // User email'i al
      const userEmail = auth.currentUser?.email;
      if (!userEmail) {
        throw new Error('Kullanıcı oturumu bulunamadı');
      }

      // Her iki şantiyenin puantajlarını al - user-based path kullan
      const timeSheetsRef = collection(db, `users/${userEmail}/timeSheets`);
      const puantajQuery = query(
        timeSheetsRef,
        where('Müşteri Adı', '==', conflictingSantiye['Şantiye Adı'])
      );
      const puantajlarSnapshot = await getDocs(puantajQuery);

      console.log(`📊 Çakışan şantiyenin puantaj sayısı: ${puantajlarSnapshot.size}`);

      // Makine saatlik ücret bilgisini alma fonksiyonu
      const getMachineHourlyRate = async (machineName) => {
        try {
          const machinesRef = collection(db, `users/${userEmail}/machines`);
          const machineQuery = query(machinesRef, where('MakineAdı', '==', machineName));
          const machineSnapshot = await getDocs(machineQuery);
          
          if (!machineSnapshot.empty) {
            const machineData = machineSnapshot.docs[0].data();
            const hourlyRate = parseFloat(machineData.BirimFiyat || machineData['Birim Fiyat'] || '0');
            console.log(`🔧 Makine saatlik ücreti bulundu: ${machineName} = ${hourlyRate} TL/saat`);
            return hourlyRate;
          } else {
            console.warn(`⚠️ Makine bulunamadı: ${machineName}`);
            return 0;
          }
        } catch (error) {
          console.error('❌ Makine saatlik ücreti alınırken hata:', error);
          return 0;
        }
      };

      // İyileştirilmiş puantaj tutarı hesaplama fonksiyonu
      const calculatePuantajAmount = async (puantajData) => {
        console.log('💰 Şantiye puantaj tutar hesaplaması başlıyor:', puantajData);
        
        // Çalışma saati bilgisini farklı field adlarından al
        let workingTime = puantajData['Toplam Çalışma Süresi'] || 
                         puantajData.totalWorkingTime || 
                         puantajData['Total Working Time'] ||
                         puantajData['toplamCalisSuresi'] ||
                         puantajData['toplam_calista_suresi'] ||
                         '0:00';
        
        // Eğer toplam çalışma süresi yoksa, saat bilgilerinden hesapla
        if (!workingTime || workingTime === '0:00' || workingTime === '00:00') {
          console.log('📊 Toplam çalışma süresi bulunamadı, saat bilgilerinden hesaplanıyor...');
          
          let totalMinutes = 0;
          
          // 1. Vardiya
          const start1 = puantajData['1. Başlangıç Saati'] || puantajData['startTime1'] || puantajData['start1'];
          const end1 = puantajData['1. Bitiş Saati'] || puantajData['endTime1'] || puantajData['end1'];
          
          if (start1 && end1) {
            const duration1 = calculateTimeDuration(start1, end1);
            totalMinutes += duration1;
            console.log(`⏰ 1. Vardiya: ${start1} - ${end1} = ${duration1} dakika`);
          }
          
          // 2. Vardiya
          const start2 = puantajData['2. Başlangıç Saati'] || puantajData['startTime2'] || puantajData['start2'];
          const end2 = puantajData['2. Bitiş Saati'] || puantajData['endTime2'] || puantajData['end2'];
          
          if (start2 && end2) {
            const duration2 = calculateTimeDuration(start2, end2);
            totalMinutes += duration2;
            console.log(`⏰ 2. Vardiya: ${start2} - ${end2} = ${duration2} dakika`);
          }
          
          // 3. Vardiya (eğer varsa)
          const start3 = puantajData['3. Başlangıç Saati'] || puantajData['startTime3'] || puantajData['start3'];
          const end3 = puantajData['3. Bitiş Saati'] || puantajData['endTime3'] || puantajData['end3'];
          
          if (start3 && end3) {
            const duration3 = calculateTimeDuration(start3, end3);
            totalMinutes += duration3;
            console.log(`⏰ 3. Vardiya: ${start3} - ${end3} = ${duration3} dakika`);
          }
          
          // Hesaplanan toplam süreyi HH:MM formatına çevir
          const hours = Math.floor(totalMinutes / 60);
          const minutes = totalMinutes % 60;
          workingTime = `${hours}:${minutes.toString().padStart(2, '0')}`;
          console.log(`📊 Hesaplanan toplam çalışma süresi: ${workingTime} (${totalMinutes} dakika)`);
        }
        
        // Saatlik ücret bilgisini farklı field adlarından al
        let hourlyRate = parseFloat(
          puantajData['Saatlik Ücret'] || 
          puantajData.hourlyRate || 
          puantajData['Hourly Rate'] ||
          puantajData['saatlikUcret'] ||
          puantajData['saatlik_ucret'] ||
          puantajData['BirimFiyat'] ||
          puantajData['birimFiyat'] ||
          puantajData['birim_fiyat'] ||
          '0'
        );
        
        // Eğer saatlik ücret bulunamazsa makine bilgilerinden çek
        if (hourlyRate === 0) {
          console.log('💰 Puantajda saatlik ücret bulunamadı, makine bilgilerinden çekiliyor...');
          const machineName = puantajData['Makine İsmi'] || puantajData['machineName'] || puantajData['Makine Adı'];
          if (machineName) {
            hourlyRate = await getMachineHourlyRate(machineName);
          }
        }
        
        console.log(`💰 Saatlik ücret: ${hourlyRate} TL`);
        
        // Çalışma saatini hesapla - iOS HopsPlus ile AYNI MANTIK
        const totalMinutes = parseWorkingTime(workingTime);
        const totalHours = totalMinutes / 60;
        
        const totalAmount = totalHours * hourlyRate;
        
        console.log(`📊 Şantiye tutar hesaplama sonucu: ${totalHours} saat × ${hourlyRate} TL = ${totalAmount} TL`);
        
        return totalAmount;
      };
      
      // Saat aralığından dakika hesaplama yardımcı fonksiyonu
      const calculateTimeDuration = (startTime, endTime) => {
        if (!startTime || !endTime) return 0;
        
        const parseTime = (timeStr) => {
          const [hours, minutes] = timeStr.split(':').map(Number);
          if (isNaN(hours) || isNaN(minutes)) return null;
          return hours * 60 + minutes;
        };
        
        const startMinutes = parseTime(startTime);
        const endMinutes = parseTime(endTime);
        
        if (startMinutes === null || endMinutes === null) return 0;
        
        // Gece yarısı geçişi kontrolü
        let duration = endMinutes - startMinutes;
        if (duration < 0) {
          duration += 24 * 60; // Ertesi güne geçiş
        }
        
        return duration;
      };

      // Aktarılan puantajların detaylarını topla (cari geçmişi için)
      const transferredPuantajlar = [];

      // CARİ KASA BORÇ BİRLEŞTİRME İŞLEMİ
      console.log('💰 Cari kasa borç birleştirme işlemi başlıyor...');
      
      // Çakışan şantiyenin borç bilgilerini al
      const conflictingDebtRef = doc(db, `users/${userEmail}/customerList/${conflictingSantiye.id}/debt/current`);
      const conflictingDebtDoc = await getDoc(conflictingDebtRef);
      
      let conflictingDebtData = null;
      let conflictingAmount = 0;
      let conflictingHistory = [];
      
      if (conflictingDebtDoc.exists()) {
        conflictingDebtData = conflictingDebtDoc.data();
        conflictingAmount = conflictingDebtData?.amount || 0;
        conflictingHistory = conflictingDebtData?.history || [];
      }
      
      console.log(`💰 Çakışan şantiye borcu: ${conflictingAmount} TL (${conflictingHistory.length} işlem)`);

      // Yeni şantiye verilerini hazırla
      const newSantiyeData = {
        'Müşteri Adı': santiyeName.trim(),
        'Şantiye Adı': santiyeName.trim(),
        Telefon: santiyePhone.trim(),
        'E-posta': santiyeEmail.trim(),
        cariCode: santiyeCariCode.trim(),
        'Şantiye Cari Kodu': santiyeCariCode.trim(),
        Şantiye: true,
        parentId: selectedCustomer.id,
        Onay: 'Onaylandı',
        updatedAt: new Date().toISOString(),
        mergedWith: conflictingSantiye['Şantiye Adı'] // Hangi şantiye ile birleştirildiği
      };

      // Çakışan şantiyeyi güncelle - user-based path kullan
      const conflictingSantiyeRef = doc(db, `users/${userEmail}/customerList`, conflictingSantiye.id);
      batch.update(conflictingSantiyeRef, newSantiyeData);

      // Tüm puantajları güncelle ve detaylarını kaydet
      for (const puantajDoc of puantajlarSnapshot.docs) {
        const puantajData = puantajDoc.data();
        
        // Puantaj detaylarını kaydet (cari geçmişi için) - async olarak hesapla
        const totalAmount = await calculatePuantajAmount(puantajData) || 0;
        
        transferredPuantajlar.push({
          id: puantajDoc.id,
          puantajCount: puantajData['Puantaj No'] || puantajData.puantajCount || 'Bilinmeyen',
          machineName: puantajData['Makine İsmi'] || puantajData.machineName || 'Bilinmeyen',
          totalWorkingTime: puantajData['Toplam Çalışma Süresi'] || puantajData.totalWorkingTime || '0:00',
          date: puantajData['Tarih'] || puantajData.date || new Date().toISOString().split('T')[0],
          hourlyRate: puantajData['Saatlik Ücret'] || puantajData.hourlyRate || 0,
          totalAmount: totalAmount
        });
        
        batch.update(puantajDoc.ref, {
          'Müşteri Adı': santiyeName.trim(),
          'Cari Kodu': santiyeCariCode.trim(),
          'mergedFrom': conflictingSantiye['Şantiye Adı'], // Hangi şantiyeden birleştirildiği
          'mergedAt': new Date().toISOString(),
          'mergedBy': userEmail,
          'mergedReason': `🏗️ Şantiye birleştirme: "${conflictingSantiye['Şantiye Adı']}" → "${santiyeName.trim()}"`,
          'originalSantiyeName': conflictingSantiye['Şantiye Adı'] // Orijinal şantiye adını sakla
        });
      }

      // Çakışan şantiyenin borç bilgilerini yeni şantiyeye aktarma
      if (conflictingAmount > 0 || conflictingHistory.length > 0) {
        // Çakışan şantiyenin history'sini transfer olarak işaretle
        const transferredHistory = conflictingHistory.map(historyItem => ({
          ...historyItem,
          transferredFrom: conflictingSantiye['Şantiye Adı'],
          transferredTo: santiyeName.trim(),
          transferredAt: new Date().toISOString(),
          transferredBy: userEmail
        }));
        
        // Transfer bilgi kaydı oluştur
        const transferInfoEntry = {
          id: `merge_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          amount: 0, // Sıfır - ekstra borç yaratmaz, sadece bilgi amaçlı
          date: new Date().toISOString(),
                     description: `🏗️ ŞANTİYE BİRLEŞTİRME: "${conflictingSantiye['Şantiye Adı']}" şantiyesinden "${santiyeName.trim()}" şantiyesine aktarım (Toplam: ${conflictingAmount} TL, İşlem: ${conflictingHistory.length} adet)`,
          type: 'transfer_in',
          transferFrom: conflictingSantiye['Şantiye Adı'],
          transferTo: santiyeName.trim(),
          isInfoOnly: true, // Sadece bilgi amaçlı işaret
          mergeOperation: true
        };
        
        // Aktarılan puantajlar için cari geçmişi kayıtları oluştur
        const puantajHistoryEntries = transferredPuantajlar.map(puantaj => {
          // Tarihi formatla (DD.MM.YYYY -> ISO)
          let puantajDate = puantaj.date;
          if (puantaj.date && puantaj.date.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
            const [day, month, year] = puantaj.date.split('.');
            puantajDate = new Date(`${year}-${month}-${day}`).toISOString();
          } else if (puantaj.date && !puantaj.date.includes('T')) {
            puantajDate = new Date(puantaj.date + 'T12:00:00.000Z').toISOString();
          }
          
          return {
            id: `transferred_puantaj_${puantaj.id}_${Date.now()}`,
            amount: puantaj.totalAmount,
            date: puantajDate,
            description: `📊 PUANTAJ AKTARIM: ${puantaj.puantajCount} - ${puantaj.machineName} (${puantaj.totalWorkingTime}) | "${conflictingSantiye['Şantiye Adı']}" → "${santiyeName.trim()}"`,
            type: 'increase',
            transferFrom: conflictingSantiye['Şantiye Adı'],
            transferTo: santiyeName.trim(),
            transferredAt: new Date().toISOString(),
            transferredBy: userEmail,
            // Puantaj detayları
            puantajCode: puantaj.puantajCount,
            machineName: puantaj.machineName,
            machineHours: puantaj.totalWorkingTime,
            hourlyRate: puantaj.hourlyRate,
            originalPuantajId: puantaj.id,
            transferType: 'puantaj_merge',
            mergeOperation: true
          };
        });
        
        // Yeni şantiyenin borç bilgilerini oluştur
        const newSantiyeHistory = [
          transferInfoEntry, // Transfer bilgi kaydı
          ...transferredHistory, // Çakışan şantiyenin gerçek işlemleri
          ...puantajHistoryEntries // Aktarılan puantajların detayları
        ];
        
        // Yeni şantiyenin borç bilgilerini kaydet
        const newSantiyeDebtRef = doc(db, `users/${userEmail}/customerList/${conflictingSantiye.id}/debt/current`);
        const newSantiyeDebtData = {
          amount: conflictingAmount, // Çakışan şantiyenin borcu yeni şantiyeye geçer
          history: newSantiyeHistory,
          lastUpdated: new Date().toISOString(),
          customerName: santiyeName.trim(),
          mergedFrom: conflictingSantiye['Şantiye Adı'],
          mergedAt: new Date().toISOString()
        };
        
        batch.set(newSantiyeDebtRef, newSantiyeDebtData);
        
        console.log(`💰 Cari kasa birleştirme hazırlandı: ${conflictingAmount} TL yeni şantiyeye aktarıldı`);
      }

      await batch.commit();

      console.log('✅ Şantiye birleştirme işlemi tamamlandı:', {
        birleştirilenPuantajSayısı: puantajlarSnapshot.size,
        yeniŞantiye: santiyeName.trim(),
        çakışanŞantiyeGüncellendi: conflictingSantiye['Şantiye Adı'],
        birleştirilenBorç: conflictingAmount,
        birleştirilenİşlemSayısı: conflictingHistory.length
      });

      // Detaylı başarı mesajı oluştur
      let detailedMessage = `🎉 ŞANTİYE BİRLEŞTİRME TAMAMLANDI!\n\n`;
      
      // Şantiye bilgileri
      detailedMessage += `📋 SONUÇ:\n`;
      detailedMessage += `• "${conflictingSantiye['Şantiye Adı']}" şantiyesi güncellendi\n`;
      detailedMessage += `• Yeni adı: "${santiyeName.trim()}"\n`;
      detailedMessage += `• Yeni cari kodu: "${santiyeCariCode.trim()}"\n\n`;
      
      // Puantaj bilgileri
      if (puantajlarSnapshot.size > 0) {
        const totalPuantajAmount = transferredPuantajlar.reduce((sum, p) => sum + p.totalAmount, 0);
        
        detailedMessage += `📊 PUANTAJ AKTARİMİ:\n`;
        detailedMessage += `• ${puantajlarSnapshot.size} adet puantaj güncellendi\n`;
        detailedMessage += `• Toplam puantaj tutarı: ${totalPuantajAmount.toLocaleString('tr-TR')} TL\n`;
        detailedMessage += `• Eski şantiye adı: "${conflictingSantiye['Şantiye Adı']}"\n`;
        detailedMessage += `• Yeni şantiye adı: "${santiyeName.trim()}"\n`;
        detailedMessage += `• Puantajlarda "mergedFrom" etiketi ile eski adı gösterildi\n`;
        detailedMessage += `• Her puantaj cari kasa geçmişinde ayrı ayrı görünecek\n\n`;
        
        // Aktarılan puantajların listesi
        if (transferredPuantajlar.length > 0) {
          detailedMessage += `📋 AKTARILAN PUANTAJLAR:\n`;
          transferredPuantajlar.forEach((puantaj, index) => {
            detailedMessage += `  ${index + 1}. ${puantaj.puantajCount} - ${puantaj.machineName} (${puantaj.totalWorkingTime}) - ${puantaj.totalAmount.toLocaleString('tr-TR')} TL\n`;
          });
          detailedMessage += `\n`;
        }
      } else {
        detailedMessage += `📊 PUANTAJ: Güncellenecek puantaj bulunamadı\n\n`;
      }
      
      // Cari kasa bilgileri
      if (conflictingAmount > 0 || conflictingHistory.length > 0) {
        detailedMessage += `💰 CARİ KASA AKTARİMİ:\n`;
        if (conflictingAmount > 0) {
          detailedMessage += `• ${conflictingAmount.toLocaleString('tr-TR')} TL borç yeni şantiyeye aktarıldı\n`;
        }
        if (conflictingHistory.length > 0) {
          detailedMessage += `• ${conflictingHistory.length} adet işlem geçmişi aktarıldı\n`;
          detailedMessage += `• Cari kasa geçmişinde "şantiye birleştirme" olarak görünecek\n`;
        }
        detailedMessage += `• Eski şantiye: "${conflictingSantiye['Şantiye Adı']}"\n`;
        detailedMessage += `• Yeni şantiye: "${santiyeName.trim()}"\n\n`;
      } else {
        detailedMessage += `💰 CARİ KASA: Aktarılacak borç bulunamadı\n\n`;
      }
      
      // Kontrol etme talimatları
      detailedMessage += `🔍 KONTROL EDİN:\n`;
      detailedMessage += `• Puantajlar sayfasında "${santiyeName.trim()}" şantiyesinin puantajlarını kontrol edin\n`;
      detailedMessage += `• Cari Kasa sayfasında "${santiyeName.trim()}" şantiyesinin borç durumunu kontrol edin\n`;
      detailedMessage += `• Müşteri Listesi'nde "${santiyeName.trim()}" şantiyesinin güncellendiğini kontrol edin\n`;
      detailedMessage += `• Transfer edilen işlemler geçmişte "aktarıldı" etiketiyle görünecek`;

      setSuccessMessage(detailedMessage);
      setAlertOpen(true);
      onClose();
      resetForm();
    } catch (error) {
      console.error('❌ Şantiye birleştirme hatası:', error);
      setError(`Şantiye birleştirme sırasında hata: ${error.message}`);
      setAlertOpen(true);
    }
  };

  return (
    <>
      <Modal open={isOpen} onClose={onClose}>
        <ModalBox>
          <Typography variant="h6" gutterBottom>
            Yeni Şantiye Ekle
          </Typography>
          <Box display="flex" alignItems="center">
            <TextField
              label="Şantiye Adı"
              fullWidth
              margin="normal"
              value={santiyeName}
              onChange={(e) => setSantiyeName(e.target.value)}
            />
            <StyledIconButton
              onClick={async () => {
                const newState = !isCustomerSelectionOpen;
                setIsCustomerSelectionOpen(newState);
                if (newState) {
                  await fetchPendingCustomers();
                }
              }}
              size="small"
            >
              <ArrowDropDownIcon fontSize="small" />
            </StyledIconButton>
          </Box>
          {isCustomerSelectionOpen && (
            <CustomerSelectionTable
              customers={pendingCustomers}
              onSelectCustomer={handleSelectCustomer}
              enableAutoUppercase={true}
            />
          )}
          <TextField
            label="Telefon"
            fullWidth
            margin="normal"
            value={santiyePhone}
            onChange={(e) => setSantiyePhone(e.target.value)}
          />
          <TextField
            label="E-posta"
            fullWidth
            margin="normal"
            value={santiyeEmail}
            onChange={(e) => setSantiyeEmail(e.target.value)}
          />
          <TextField
            label="Cari Kodu"
            fullWidth
            margin="normal"
            value={santiyeCariCode}
            disabled
          />
          <Box mt={2} display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              color="primary"
              onClick={handleAddSantiye}
            >
              Ekle
            </Button>
          </Box>
        </ModalBox>
      </Modal>

      <ConflictResolutionModal
        isOpen={isConflictModalOpen}
        onClose={() => setIsConflictModalOpen(false)}
        onConfirm={handleConflictResolution}
        conflictingCustomer={conflictingSantiye}
        newCustomerData={{
          'Şantiye Adı': santiyeName,
          'Şantiye Cari Kodu': santiyeCariCode,
        }}
        message="Bu isimde bir şantiye zaten var. Mevcut şantiyeyi güncellemek ve verileri aktarmak ister misiniz?"
      />
    </>
  );
};

export default AddSantiyeModal;

